import { useParams } from "react-router-dom";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getSenderInfo,
  getFile,
  approveSender,
} from "services/customerManagement";
import ContentCard from "Components/shared/ContentCard";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import LoadingBanner from "Components/shared/LoadingBanner";
import formatHours from "utils/formatHours";
import { useRTL } from "hooks/isRTL";
import { useState } from "react";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import { businessTypes, fileTypeEnum } from "../../../enums/shared";
import { fallbackValue } from "constants/fallbackConstant";
import formatDateAndTime from "utils/formatDateAndTime";

const SenderInfoTab = () => {
  const { senderId } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    message: "",
    severity: "",
  });
  const { t } = useRTL();

  const {
    data: senderData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["senderInfo", senderId],
    queryFn: () => getSenderInfo(senderId),
    enabled: !!senderId,
  });

  const { mutate: approveSenderAction } = useMutation({
    mutationFn: () => approveSender(senderId),
    onSuccess: () => {
      setSnackbarDetails({
        message: t("Sender approved successfully!"),
        severity: "success",
      });
    },
    onError: () => {
      setSnackbarDetails({
        message: t("Failed to approve sender. Please try again."),
        severity: "error",
      });
    },
  });

  const handleFileClick = async (fileId) => {
    try {
      const fileBlob = await getFile(fileId);

      const fileURL = window.URL.createObjectURL(fileBlob);
      window.open(fileURL, "_blank");
    } catch (error) {
      console.error("Failed to fetch file:", error);
    }
  };

  const handleApprove = () => {
    approveSenderAction();
    setDialogOpen(false);
  };

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  const handleSnackbarClose = () => {
    setSnackbarDetails({ message: "", severity: "" });
  };

  if (isLoading)
    return <LoadingBanner isLoading={true} message={t("loading")} />;
  if (isError)
    return <Typography color="error">Failed to load data.</Typography>;

  const capitalizeFirst = (text) =>
    typeof text === "string" && text.length
      ? text.charAt(0).toUpperCase() + text.slice(1)
      : fallbackValue;

  const content = [
    { label: t("fullName"), value: senderData?.fullName || fallbackValue },
    {
      label: t("phoneNumber"),
      value: senderData?.phoneNumber?.replace("_", "") || fallbackValue,
    },
    { label: t("email"), value: senderData?.email || fallbackValue },
    {
      label: t("businessType"),
      value: t(businessTypes[senderData?.busnissType]) || fallbackValue,
    },
    {
      label: t("nationalNumber"),
      value: senderData?.nationalNumber || fallbackValue,
    },
    { label: t("website"), value: senderData?.website || fallbackValue },
    {
      label: t("address"),
      value: capitalizeFirst(senderData?.address) || fallbackValue,
    },
    {
      label: t("openHours"),
      value: formatHours(senderData?.openHours),
    },
    {
      label: t("creationDate"),
      value: formatDateAndTime(senderData?.creationDate),
    },
  ]; //mark
  const fileTypeLabelMap = Object.keys(fileTypeEnum).reduce((acc, key) => {
    acc[key] = t(fileTypeEnum[key]);
    return acc;
  }, {});

  // If zero fees dates exist, show them in the details
  if (senderData?.zeroStartDate) {
    content.push({
      label: t("Zero Fees Start Date"),
      value: formatDateAndTime(senderData.zeroStartDate),
    });
  }

  if (senderData?.zeroEndDate) {
    content.push({
      label: t("Zero Fees End Date"),
      value: formatDateAndTime(senderData.zeroEndDate),
    });
  }

  const status = senderData?.status;

  return (
    <Box sx={{ padding: "1rem" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "2rem",
        }}
      >
        <ContentCard title={t("businessInfo")} content={content} />

        <Paper
          elevation={2}
          sx={{
            flex: 1,
            padding: "1.5rem",
            backgroundColor: "background.white",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              marginBottom: "1rem",
              color: "text.black",
              fontSize: "1.2rem",
            }}
          >
            {t("files")}
          </Typography>

          <List sx={{ flexGrow: 1 }}>
            {senderData?.files?.map((file, index) => (
              <ListItem
                key={file.id}
                sx={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                  borderBottom: "1px solid #e0e0e0",
                  padding: "0.5rem 0 0.75rem ",
                  marginBottom: "0.5rem",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    color: "text.black",
                    fontSize: "1rem",
                    minWidth: "80px",
                  }}
                >
                  {fileTypeLabelMap[file.fileType]}
                </Typography>
                <IconButton
                  onClick={() => handleFileClick(file.id)}
                  sx={{ color: "primary.main" }}
                >
                  <InsertDriveFileIcon />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.9rem",
                  }}
                >
                  {`${(file.size / 1024).toFixed(1)} KB`}
                </Typography>
              </ListItem>
            ))}
          </List>

          {/* only show the button if not prevoulsy approved */}
          {status === 3 && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#2A3467",
                "&:hover": {
                  backgroundColor: "#1f284f",
                },
                marginTop: "1.5rem",
              }}
              onClick={handleOpenDialog}
            >
              {t("approve")}
            </Button>
          )}
        </Paper>
      </Box>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{t("confirmAction")}</DialogTitle>
        <DialogContent>
          <Typography>{t("areYouSureYouWantToApprove")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleCloseDialog}>
            {t("decline")}
          </Button>
          <Button variant="contained" color="success" onClick={handleApprove}>
            {t("approve")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <CustomSnackbar
        message={snackbarDetails.message}
        severity={snackbarDetails.severity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default SenderInfoTab;
